// Persona model — the single source of truth for the staff access tokens,
// shared by the web shell/session (apps/web) and the server (apps/server).
//
// Two distinct vocabularies live here (ADR-0004):
//
//   - FrontendKey  — the four web shells a user can land on / switch between
//                    (`desk`, `ops`, `comply`, `view`). One route tree each.
//   - PersonaToken — every token that can sit in `users.personas[]`. A superset
//                    of FrontendKey: it adds `admin` (the firm app-configuration
//                    / all-access role — "SuperAdmin" in product conversation)
//                    and `im-assistant` (a restricted Desk persona, mutually
//                    exclusive with `desk`).
//
// `Persona` (the active-persona ACL) is `FrontendKey | 'im-assistant'` — the
// active persona that governs every read and route admission. `admin` is never
// an *active* persona on the web shell (the SuperAdmin operates as `view`); it
// is a granted token that opens the Admin shell (`STAFF_SHELL_KEYS`) and
// auto-passes route admission.
//
// This module is pure (no I/O, no framework) so both apps consume the SAME
// constants and the SAME policy rather than each maintaining a parallel copy.

/** The four staff web shells, in canonical landing order. */
export const FRONTEND_KEYS = ['desk', 'ops', 'comply', 'view'] as const;
export type FrontendKey = (typeof FRONTEND_KEYS)[number];

/**
 * The staff shells of the web, in the order of the switcher: the four
 * frontends, then the Admin shell. The Admin shell is not a persona. It opens
 * on the granted `admin` token, and the active persona does not change when
 * the user opens it (ADR-0004).
 */
export const STAFF_SHELL_KEYS = [...FRONTEND_KEYS, 'admin'] as const;
export type StaffShellKey = (typeof STAFF_SHELL_KEYS)[number];

export function isStaffShellKey(value: string): value is StaffShellKey {
    return (STAFF_SHELL_KEYS as readonly string[]).includes(value);
}

/**
 * The active persona that governs reads + route admission. A superset of
 * `FrontendKey`: `im-assistant` is the first active persona that is not itself
 * a frontend (it drives the Desk shell via `personaToFrontend`).
 */
export type Persona = FrontendKey | 'im-assistant';

/**
 * Every token that may appear in `users.personas[]`. Adds the non-frontend
 * grants `admin` and `im-assistant` to the four frontend keys.
 */
export const PERSONA_TOKENS = [...FRONTEND_KEYS, 'admin', 'im-assistant'] as const;
export type PersonaToken = (typeof PERSONA_TOKENS)[number];

/** The non-frontend grants — tokens that are not themselves a web shell. */
export const ADMIN_TOKEN = 'admin' as const;
export const IM_ASSISTANT_TOKEN = 'im-assistant' as const;

/**
 * The Desk sidebar section keys an IM Assistant is BARRED from. Everything else
 * on the Desk sidebar is allowed. The bar is enforced at the data gate (the
 * Object Types behind these pages do not carry the `im-assistant` tag) and at
 * route admission — the sidebar omission is cosmetic reinforcement only.
 *
 * `desk-vulnerability` left the set with the Dowgate ruling of 2026-09-30: the
 * assistant works the Article 9 vulnerability assessment with the access of the
 * investment manager (ADR-0016).
 * The three-tier client-model pages (`contacts`, `suitability-records` and
 * `relationships`) are open to the assistant. The acting-for scope has landed
 * (SEP-199), and the tier Object Types tag `im-assistant` with a book row
 * scope. The Dowgate ruling of 2026-09-30 gives the assistant the same access
 * as the investment manager over the whole onboarding process. The comply-only
 * assessment content of `suitability-record-assessment` stays behind the
 * compliance wall (ADR-0015 D5).
 */
export const IM_ASSISTANT_BARRED_DESK_SECTIONS = [
    // `suitability` used to sit here. SEP-243 section 30 admitted it: the EA
    // PREPARES the Internal Review, populates it and actions the review
    // feedback. The EA never signs the review off, and the bar on the sign-off
    // is the Action itself (`SignOffSuitabilityAssessment` admits `desk` alone,
    // and it holds the actor to the responsible investment manager).
    // SEP-243 sections 31 and 32 - the peer review queue. A peer review is the
    // judgement of an investment manager on the work of another investment
    // manager. `RecordSuitabilityPeerReview` admits `desk` alone, and the read
    // grant of section 31 opens for a `desk` actor alone, so an assistant would
    // meet an empty page. The bar is the honest answer.
    'peer-reviews',
    // `benchmarking` used to sit here. SEP-199 admitted it: the assistant now
    // resolves onto the senior manager's book in the server `DeskScope`, and
    // `ClientPeerBenchmarkMonth` tags `im-assistant`, so the page shows the
    // manager's rows rather than the firm.
] as const;

export function isFrontendKey(value: string): value is FrontendKey {
    return (FRONTEND_KEYS as readonly string[]).includes(value);
}

export function isPersonaToken(value: string): value is PersonaToken {
    return (PERSONA_TOKENS as readonly string[]).includes(value);
}

export function isActivePersona(value: string): value is Persona {
    return isFrontendKey(value) || value === IM_ASSISTANT_TOKEN;
}

/**
 * Map an active persona to the web shell it drives. Identity for the four
 * frontend keys; `im-assistant → desk` (the assistant works the Desk shell,
 * on a restricted page set).
 */
export function personaToFrontend(persona: Persona): FrontendKey {
    return persona === IM_ASSISTANT_TOKEN ? 'desk' : persona;
}

/**
 * The frontends a granted token set may land on / switch between, in canonical
 * order. `admin` is not a frontend, so it contributes nothing here (it opens
 * the Admin shell instead, see `grantedShells`). `im-assistant` resolves to `desk` so an
 * `im-assistant`-only user is NOT locked out — they land on the Desk shell.
 *
 * De-duplicated: a user granted both `desk` and `im-assistant` (which
 * normalisation forbids, but a raw seed row might carry) yields `desk` once.
 */
export function grantedFrontends(grantedTokens: readonly string[]): FrontendKey[] {
    const out: FrontendKey[] = [];
    for (const key of FRONTEND_KEYS) {
        const granted =
            grantedTokens.includes(key) ||
            (key === 'desk' && grantedTokens.includes(IM_ASSISTANT_TOKEN));
        if (granted) out.push(key);
    }
    return out;
}

/** Whether the granted token set opens the Admin shell. */
export function grantsAdministration(grantedTokens: readonly string[]): boolean {
    return grantedTokens.includes(ADMIN_TOKEN);
}

/**
 * The staff shells a granted token set may open, in switcher order: the
 * granted frontends, then `admin` when the set holds the `admin` token.
 */
export function grantedShells(grantedTokens: readonly string[]): StaffShellKey[] {
    const shells: StaffShellKey[] = grantedFrontends(grantedTokens);
    if (grantsAdministration(grantedTokens)) shells.push('admin');
    return shells;
}

/**
 * Decide whether a Desk sidebar section key is visible to the given active
 * persona. Every persona sees every Desk section EXCEPT an `im-assistant`,
 * who is barred from the suitability + vulnerability pages.
 */
export function isDeskSectionVisible(persona: Persona, sectionKey: string): boolean {
    if (persona !== IM_ASSISTANT_TOKEN) return true;
    return !(IM_ASSISTANT_BARRED_DESK_SECTIONS as readonly string[]).includes(sectionKey);
}

/**
 * The Desk ROUTES an IM Assistant is barred from, below the level of a sidebar
 * section.
 *
 * A section bar is the usual answer, and it is the right one when a whole page
 * tree is out of bounds. This list is for a page that sits UNDER an allowed
 * section and still holds data the assistant must not write.
 *
 * The list is empty. `/desk/suitability/vulnerability` left it with the Dowgate
 * ruling of 2026-09-30: the assistant fills the Article 9 vulnerability
 * assessment with the access of the investment manager, and the server audits
 * each write under the identity of the assistant (ADR-0016). The mechanism
 * stays, so a later ruling can bar a route again.
 */
export const IM_ASSISTANT_BARRED_DESK_ROUTES: readonly string[] = [];

/**
 * True when this active persona is barred from this pathname by
 * {@link IM_ASSISTANT_BARRED_DESK_ROUTES}. Matched on a segment boundary, so
 * `/desk/suitability/vulnerability-notes` is NOT barred by the entry above.
 */
export function isDeskRouteBarred(persona: Persona, pathname: string): boolean {
    if (persona !== IM_ASSISTANT_TOKEN) return false;
    return IM_ASSISTANT_BARRED_DESK_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
}

/**
 * The persona-access policy outcome consumed by the web shell.
 *
 * - `grantedFrontends` — shells the user may switch between (sidebar switcher,
 *   `/` landing). `im-assistant` resolves to `desk`.
 * - `frontend` — the shell the ACTIVE persona drives (`im-assistant → desk`).
 * - `showAdministration` — open the Admin shell. Driven by the GRANTED `admin`
 *   token, not the active persona.
 */
export interface PersonaPolicy {
    readonly grantedFrontends: FrontendKey[];
    readonly frontend: FrontendKey;
    readonly showAdministration: boolean;
}

/**
 * Resolve the full persona-access policy for a session. Pure: granted tokens +
 * active persona in, policy out. Replaces the inline `grantedFrontends` filter
 * and the per-frontend section call the web shell used to scatter.
 */
export function resolvePersonaPolicy(
    grantedTokens: readonly string[],
    activePersona: Persona,
): PersonaPolicy {
    return {
        grantedFrontends: grantedFrontends(grantedTokens),
        frontend: personaToFrontend(activePersona),
        showAdministration: grantsAdministration(grantedTokens),
    };
}

// --- Role normalisation -----------------------------------------------------

/**
 * Outcome of normalising a requested persona-token set for add-user /
 * role-replace. `ok` carries the normalised token list (de-duplicated, in
 * canonical order); `error` carries a machine code + human message.
 *
 * The same rules run on BOTH the UI (the None / Desk / IM Assistant segmented
 * control + view checkboxes) and the server (independently, on every write) —
 * the server never trusts the client to have applied them.
 */
export type RoleNormalisation =
    | { ok: true; personas: PersonaToken[] }
    | { ok: false; code: RoleNormalisationError; message: string };

export type RoleNormalisationError =
    | 'empty'
    | 'unknown_token'
    | 'admin_not_assignable'
    | 'desk_im_exclusive';

/**
 * Validate + normalise a requested persona-token set (add-user / role-replace).
 *
 * Rules (ADR-0004):
 *   - at least one token (`empty`);
 *   - every token must be a known `PersonaToken` (`unknown_token`);
 *   - `admin` is never assignable through the UI path (`admin_not_assignable`)
 *     — minting a SuperAdmin stays a seed/db task, off the privilege-escalation
 *     path;
 *   - `desk` and `im-assistant` are mutually exclusive (`desk_im_exclusive`).
 *
 * Idempotent: feeding a normalised set back in returns the same set. Output is
 * de-duplicated and ordered per `PERSONA_TOKENS`.
 */
export function normalisePersonaTokens(requested: readonly string[]): RoleNormalisation {
    if (requested.length === 0) {
        return { ok: false, code: 'empty', message: 'Select at least one role.' };
    }

    const seen = new Set<PersonaToken>();
    for (const token of requested) {
        if (!isPersonaToken(token)) {
            return {
                ok: false,
                code: 'unknown_token',
                message: `Unknown role '${token}'.`,
            };
        }
        if (token === ADMIN_TOKEN) {
            return {
                ok: false,
                code: 'admin_not_assignable',
                message: 'The admin role is seed-managed and cannot be assigned here.',
            };
        }
        seen.add(token);
    }

    if (seen.has('desk') && seen.has(IM_ASSISTANT_TOKEN)) {
        return {
            ok: false,
            code: 'desk_im_exclusive',
            message: 'Desk and IM Assistant are mutually exclusive — choose one.',
        };
    }

    const personas = PERSONA_TOKENS.filter((t) => seen.has(t));
    return { ok: true, personas };
}

// --- Acting-for books -------------------------------------------------------

/**
 * One IM book an assistant may act on. An assistant is assigned to
 * one IM or to several, and each assignment gives one entry here.
 *
 * `label` is the short alias the sidebar shows in place of the single "Desk"
 * button — the manager alias of the IM ("JBD", "WD"), or the initials of the
 * name when the alias directory holds none. `name` is the full display name,
 * for the button title.
 *
 * The session payload carries the set, so the shell renders the switcher
 * without a second read. The server derives it from the assignment table, and
 * it never trusts a book the client sends back.
 */
export interface ActingForBook {
    /** The `users.id` of the IM whose book this entry opens. */
    readonly userId: string;
    /** The short switcher label, for example "JBD". */
    readonly label: string;
    /** The full name of the IM, or null when the directory holds none. */
    readonly name: string | null;
}

/**
 * The one IM book an assistant works now, as a bare `users.id`: the selection
 * when the assignments still hold it, else the first assignment. It returns
 * null when the list is empty, and the caller then falls back to the book of
 * the user itself.
 *
 * This is the SELECTION rule, and this is its one home. The server scopes a
 * Desk read with it, the shell highlights a switcher button with it, and the
 * Administration screen names a book owner with it. The membership test is the
 * whole security rule: an id that the directory does not assign never reaches
 * a read, so a forged cookie only ever chooses between books the directory has
 * already granted.
 *
 * A selection that is no longer assigned falls back rather than fails: an
 * admin can withdraw an assignment while the assistant holds an open session.
 */
export function selectActingForBookId(
    bookUserIds: ReadonlyArray<string>,
    selectedUserId: string | null | undefined,
): string | null {
    if (bookUserIds.length === 0) return null;
    if (selectedUserId && bookUserIds.includes(selectedUserId)) return selectedUserId;
    return bookUserIds[0] ?? null;
}

/**
 * Does this user hold a Desk book of their own? True for an IM, false for an
 * assistant, who works the book of an IM instead.
 *
 * A user who holds the `desk` persona is an IM, so they keep their own book.
 * This is true also when an admin assigns them the books of other IMs: the
 * assigned books come in addition to their own book. A user with no assigned
 * book also works their own book. Only an assistant who holds no `desk`
 * persona and has assigned books works no book of their own.
 *
 * The browser sibling of the server directory (`acting-for-directory.ts`). It
 * takes the FOLDED list that the server sends on a directory row, so the
 * browser never re-folds the graph: the server has already dropped a
 * self-reference and an edge onto a user with no book, and a re-fold in one screen and not the
 * next is exactly how the roster and the Edit Roles picker came to disagree.
 *
 * Every browser rule that asks "may this user own a row / be assigned as an
 * IM?" calls this, so the screens answer as one.
 */
export function isBookOwner(
    personas: ReadonlyArray<string>,
    actingForUserIds: ReadonlyArray<string>,
): boolean {
    return actingForUserIds.length === 0 || personas.includes('desk');
}

/**
 * Every Desk book that a user works, in switcher order. The own book of the
 * user comes first when {@link isBookOwner} is true, and the assigned books
 * follow. The list is never empty.
 *
 * Give this list to {@link selectActingForBookId}. The own book is then the
 * default book of an IM who also acts for other IMs.
 */
export function deskBookIds(
    userId: string,
    personas: ReadonlyArray<string>,
    actingForUserIds: ReadonlyArray<string>,
): string[] {
    return isBookOwner(personas, actingForUserIds)
        ? [userId, ...actingForUserIds]
        : [...actingForUserIds];
}

/**
 * The IM book an assistant reads now, as the switcher row rather than the bare
 * id. The projection of {@link selectActingForBookId} onto the labelled book
 * set the session payload carries.
 */
export function resolveActingForBook(
    books: ReadonlyArray<ActingForBook>,
    selectedUserId: string | null | undefined,
): ActingForBook | null {
    const userId = selectActingForBookId(
        books.map((b) => b.userId),
        selectedUserId,
    );
    return books.find((b) => b.userId === userId) ?? null;
}

/**
 * The short switcher label for an IM: the shortest manager alias bound to
 * them, uppercased, else the initials of their name, else a dash.
 *
 * The shortest alias is the one the firm uses as the desk code ("JBD"), while
 * the longer rows are spelling variants of the full name that ingest matches
 * on. A tie keeps the first alias, so the label is stable.
 *
 * The label must fit on one line of a small switcher button. Thus an alias
 * is a desk code only when it has no space and {@link DESK_CODE_MAX_LENGTH}
 * characters or fewer. When the shortest alias is a name ("Stuart Parkinson"),
 * the label is the initials of the name ("SP"). When the directory holds no
 * name, the label is the initials of that alias.
 */
export function actingForLabel(aliases: ReadonlyArray<string>, name: string | null): string {
    let best: string | null = null;
    for (const alias of aliases) {
        const trimmed = alias.trim();
        if (!trimmed) continue;
        if (best === null || trimmed.length < best.length) best = trimmed;
    }
    if (best && isDeskCode(best)) return best.toUpperCase();
    const initials = initialsOf(name ?? best ?? '');
    return initials || '-';
}

/** The longest alias that the switcher shows as a desk code ("JBD"). */
const DESK_CODE_MAX_LENGTH = 4;

function isDeskCode(alias: string): boolean {
    return alias.length <= DESK_CODE_MAX_LENGTH && !/\s/.test(alias);
}

function initialsOf(text: string): string {
    return text
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0]!.toUpperCase())
        .join('')
        .slice(0, 3);
}
